import { ThunderboltOutlined } from "@ant-design/icons"
import { Alert, Button, Flex, Form, Switch } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { unwrapEnvelope } from "../../../../utils/api/unwrap-envelope.ts"
import { showResponseMessage } from "../../../../utils/handlers/show-response-message.ts"
import {
  type AutomaticProjectCreationSettings,
  getAutomaticProjectCreationSettings,
  updateAutomaticProjectCreationSettings
} from "../../api/automatic-project-creation-settings.services.ts"
import { SettingsCard } from "../common/SettingsCard.tsx"

const DEFAULTS: AutomaticProjectCreationSettings = {
  auto_create_project_from_lead: false
}

export function AutomaticProjectCreationCard() {
  const { t } = useTranslation()
  const [settings, setSettings] =
    useState<AutomaticProjectCreationSettings | null>(null)
  const [saving, setSaving] = useState(false)
  // The form stays locked until the stored setting has arrived: saving an
  // untouched blank form over a failed load would wipe the real value.
  const [loadState, setLoadState] = useState<"loading" | "loaded" | "error">(
    "loading"
  )
  const [loadAttempt, setLoadAttempt] = useState(0)

  const [form] = Form.useForm<AutomaticProjectCreationSettings>()
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoadState("loading")

    void getAutomaticProjectCreationSettings().then((response) => {
      if (cancelled) return
      if (response?.ok && response.data) {
        setSettings(response.data)
        setLoadState("loaded")
      } else {
        setLoadState("error")
      }
    })

    return () => {
      cancelled = true
    }
  }, [loadAttempt])

  const initialValues = useMemo<AutomaticProjectCreationSettings>(
    () => ({
      auto_create_project_from_lead:
        settings?.auto_create_project_from_lead ??
        DEFAULTS.auto_create_project_from_lead
    }),
    [settings]
  )

  // Only resync from the server while there is no unsaved edit, so a slow
  // fetch resolving mid-toggle never discards what was picked.
  useEffect(() => {
    if (!dirty) form.setFieldsValue(initialValues)
  }, [form, initialValues, dirty])

  const handleFinish = async (values: AutomaticProjectCreationSettings) => {
    setSaving(true)
    try {
      const response = await updateAutomaticProjectCreationSettings(values)
      // A rejection is already surfaced by the shared response handler.
      if (response?.ok && response.data) {
        setSettings(
          unwrapEnvelope<AutomaticProjectCreationSettings>(response.data)
        )
        setDirty(false)
        showResponseMessage(
          response,
          t("settingsConfiguration.automaticProjectCreation.saved")
        )
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsCard
      iconWrapperClassName="bg-blue-100"
      icon={<ThunderboltOutlined className="text-blue-600" />}
      title={t("settingsConfiguration.automaticProjectCreation.title")}
      subtitle={t("settingsConfiguration.automaticProjectCreation.subtitle")}
    >
      {loadState === "error" && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message={t("settingsConfiguration.automaticProjectCreation.loadError")}
          action={
            <Button size="small" onClick={() => setLoadAttempt((n) => n + 1)}>
              {t("settingsConfiguration.automaticProjectCreation.retry")}
            </Button>
          }
        />
      )}
      <Form
        form={form}
        layout="vertical"
        className="with-default-error"
        initialValues={initialValues}
        disabled={saving || loadState !== "loaded"}
        onValuesChange={() => setDirty(true)}
        onFinish={handleFinish}
      >
        <Form.Item
          name="auto_create_project_from_lead"
          label={t("settingsConfiguration.automaticProjectCreation.toggleLabel")}
          extra={t("settingsConfiguration.automaticProjectCreation.toggleHint")}
          valuePropName="checked"
        >
          <Switch
            checkedChildren={t(
              "settingsConfiguration.automaticProjectCreation.enabled"
            )}
            unCheckedChildren={t(
              "settingsConfiguration.automaticProjectCreation.disabled"
            )}
          />
        </Form.Item>

        <Flex justify="end">
          <Button
            type="primary"
            htmlType="submit"
            loading={saving}
            disabled={!dirty || loadState !== "loaded"}
          >
            {t("settingsConfiguration.automaticProjectCreation.save")}
          </Button>
        </Flex>
      </Form>
    </SettingsCard>
  )
}
